import { defineStore } from "pinia";
import { ref } from "vue";
import { useAsyncOperation } from "Admin/js/Composables/useAsyncOperation";
import utilities from "Admin/js/Helpers/utilities";
import { useUserStore } from "Admin/js/Stores/User";

const LOCAL_DIVISION_MUTATION_TTL_MS = 60_000;

export const DIVISION_TYPE = Object.freeze({
    EXECUTIVE_OFFICE: "EXECUTIVE_OFFICE",
    DIVISION: "division",
    SUB_DIVISION: "sub-division",
});

export const DIVISION_TYPE_PLACEHOLDER = {
    [DIVISION_TYPE.EXECUTIVE_OFFICE]: "Executive Office",
    [DIVISION_TYPE.DIVISION]: "Division",
    [DIVISION_TYPE.SUB_DIVISION]: "Sub-Division",
};

/**
 * API uses enum values ("EXECUTIVE_OFFICE", "division", "sub-division"). Older code
 * indexed DIVISION_TYPE[child.type], but DIVISION_TYPE keys are DIVISION / SUB_DIVISION,
 * so values like "division" never matched and sub-divisions fell back to "division".
 */
export function normalizeDivisionType(raw) {
    if (raw === undefined || raw === null || raw === "") {
        return DIVISION_TYPE.DIVISION;
    }
    if (Object.values(DIVISION_TYPE).includes(raw)) {
        return raw;
    }
    if (Object.prototype.hasOwnProperty.call(DIVISION_TYPE, raw)) {
        return DIVISION_TYPE[raw];
    }
    return DIVISION_TYPE.DIVISION;
}

export const useDivisionStore = defineStore("division", () => {
    const { manageAsyncOperation } = useAsyncOperation();

    const showMembership = ref(false);
    const selectedNode = ref(null);
    const isLoadingData = ref(false);
    const divisions = ref({});
    const topLineDivisions = ref([]);

    let pendingLocalDivisionApiMutations = 0;
    const recentLocalMutationTrackIds = new Set();

    function beginLocalDivisionApiMutation() {
        pendingLocalDivisionApiMutations += 1;
    }

    function endLocalDivisionApiMutation(trackId) {
        pendingLocalDivisionApiMutations = Math.max(
            0,
            pendingLocalDivisionApiMutations - 1,
        );
        const id = Number(trackId);
        if (id > 0) {
            recentLocalMutationTrackIds.add(id);
            setTimeout(
                () => recentLocalMutationTrackIds.delete(id),
                LOCAL_DIVISION_MUTATION_TTL_MS,
            );
        }
    }

    function abortLocalDivisionApiMutation() {
        pendingLocalDivisionApiMutations = Math.max(
            0,
            pendingLocalDivisionApiMutations - 1,
        );
    }

    /**
     * Skip org-wide tree refetch on the tab that already applied the API result.
     * Other tabs for the same profile still refetch (action_performer_id is not tab-scoped).
     */
    function shouldSkipLiveSyncDivisionTreeRefetch({
        division,
        action_performer_id,
    }) {
        const trackId = Number(division?.id);
        if (trackId > 0 && recentLocalMutationTrackIds.has(trackId)) {
            recentLocalMutationTrackIds.delete(trackId);
            return true;
        }

        return (
            pendingLocalDivisionApiMutations > 0 &&
            useUserStore().isCurrentProfile(action_performer_id)
        );
    }

    function modifyTree(child) {
        if (child?.children?.length) {
            child.children = child.children.map((ch) => {
                return modifyTree(ch);
            });
        } else {
            child.children = [];
        }

        Object.assign(child, {
            type: normalizeDivisionType(child.type),
        });

        return getFormattedNode(child);
    }

    function generateId(prefix = "div") {
        return `${prefix}-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`;
    }

    function getFormattedNode(node, nodeId) {
        const type = normalizeDivisionType(node.type);
        return {
            id: nodeId ?? generateId(type),
            trackId: node.id,
            label: node.name,
            name: node.name,
            description: node.description,
            type,
            parentId: node.parent_id,
            headUserProfileId: node.head_user_profile_id,
            headUserProfile: node.head_user_profile ?? null,
            iconUrl: node.icon_url,
            children: node.children ?? [],
            spaceCategories:
                node.space_categories ?? node.spaceCategories ?? [],
            divisionProfiles:
                node.division_profiles ?? node.divisionProfiles ?? [],
            access: {
                edit: node.can_edit,
                assign: node.can_assign,
                add: node.can_add,
            },
        };
    }

    function getNodeById(id) {
        function findNode(data) {
            if (data.trackId === id) return data;
            if (data.children) {
                for (const child of data.children) {
                    const found = findNode(child);
                    if (found) return found;
                }
            }
            return null;
        }
        return findNode(divisions.value);
    }

    function fetchAllDivisions({
        params = "tree=true",
        noLoading = false,
    } = {}) {
        return manageAsyncOperation(
            async () => {
                const { data } = await axios.get(`/divisions?${params}`);
                if (data.data?.length) {
                    const roots = data.data;
                    const rootData =
                        roots.length === 1
                            ? roots[0]
                            : {
                                  id: null,
                                  name: "",
                                  type: "division",
                                  parent_id: null,
                                  children: roots,
                              };
                    divisions.value = modifyTree(rootData);
                }
            },
            null,
            (loading) => (isLoadingData.value = loading),
            noLoading,
        );
    }

    function addChildToNode(data, targetId, newChild) {
        if (data.id === targetId) {
            data.children.push(newChild);
            return true;
        }
        return data.children?.some((child) => {
            return addChildToNode(child, targetId, newChild);
        });
    }

    function handleValidationError(error) {
        let errorMessage = "An error occurred";

        if (error?.response?.data?.errors?.name?.[0]) {
            errorMessage = error.response.data.errors.name[0];
        } else if (error?.response?.data?.message) {
            errorMessage = error.response.data.message;
        }

        utilities().showErrorSnackbar(
            errorMessage || "Failed to update division",
        );
    }

    function findNodeByClientId(data, clientId) {
        if (!data || clientId == null) {
            return null;
        }
        if (data.id === clientId) {
            return data;
        }
        if (data.children?.length) {
            for (const child of data.children) {
                const found = findNodeByClientId(child, clientId);
                if (found) {
                    return found;
                }
            }
        }
        return null;
    }

    function destroyFakeDivisionIfNotPersisted(nodeId, parentId) {
        if (!nodeId) return;

        const node = findNodeByClientId(divisions.value, nodeId);
        if (!node) return;

        if (node.trackId) return;

        findAndDeleteNodeByParentId(parentId, nodeId);

        if (selectedNode.value?.id === nodeId) {
            selectedNode.value = null;
        }
    }

    function revertToOriginalDivisionName(node, nameBeforeRequest) {
        if (!node) return;
        const restored = nameBeforeRequest ?? node.name;
        node.name = restored;
        node.label = restored;
    }

    function createDivision(payload, nodeId) {
        beginLocalDivisionApiMutation();
        return manageAsyncOperation(
            async () => {
                const { data } = await axios.post("/divisions", payload);
                const response = data?.data;
                if (response) {
                    const formattedNode = getFormattedNode(response, nodeId);
                    findAndUpdateNodeByParentId(formattedNode);
                    endLocalDivisionApiMutation(response.id);
                    return formattedNode;
                }
                abortLocalDivisionApiMutation();
            },
            (error) => {
                abortLocalDivisionApiMutation();
                handleValidationError(error);
                destroyFakeDivisionIfNotPersisted(nodeId, payload.parent_id);
            },
        );
    }

    async function addFakeDivision() {
        if (selectedNode.value && !selectedNode.value?.trackId) return;
        const parent = selectedNode.value ?? divisions.value;
        if (!parent?.access?.add) {
            utilities().showErrorSnackbar("Permission Denied");
            return;
        }
        const targetId = selectedNode.value?.id;
        const addToRoot = !targetId || divisions.value.id === targetId;

        const type = addToRoot
            ? DIVISION_TYPE.DIVISION
            : DIVISION_TYPE.SUB_DIVISION;

        const fakeNode = getFormattedNode({
            parent_id: parent.trackId,
            type,
        });
        fakeNode.autoFocus = true;

        if (addToRoot) {
            divisions.value.children.push(fakeNode);
            return fakeNode;
        }

        addChildToNode(divisions.value, targetId, fakeNode);

        return fakeNode;
    }

    function updateDivision(payload, node) {
        const nameBeforeRequest = node?.name;
        beginLocalDivisionApiMutation();
        return manageAsyncOperation(
            async () => {
                const { data } = await axios.patch(
                    `/divisions/${node.trackId}`,
                    payload,
                );
                const response = data?.data;
                if (response) {
                    const formattedNode = getFormattedNode(response, node.id);
                    findAndUpdateNodeByParentId(formattedNode);
                    formattedNode.responseMessage = data?.message;
                    endLocalDivisionApiMutation(response.id);
                    return formattedNode;
                }
                abortLocalDivisionApiMutation();
            },
            (error) => {
                abortLocalDivisionApiMutation();
                handleValidationError(error);
                revertToOriginalDivisionName(node, nameBeforeRequest);
            },
        );
    }

    function deleteDivision(node) {
        return manageAsyncOperation(async () => {
            if (node?.trackId) {
                await axios.delete(`/divisions/${node.trackId}`);
            }
            findAndDeleteNodeByParentId(node.parentId, node.id);
            return true;
        });
    }

    function updateChildSequence(parent, orderedNodes) {
        beginLocalDivisionApiMutation();
        return manageAsyncOperation(
            async () => {
                const parentElement = getNodeById(parent.trackId);
                if (!parentElement) {
                    abortLocalDivisionApiMutation();
                    return;
                }

                const validChildIds = orderedNodes
                    .filter((node) => node.trackId)
                    .map((node) => node.trackId);

                await axios.post(`/divisions/reorder`, {
                    parent_id: parentElement.trackId,
                    child_sequence: validChildIds,
                });
                parentElement.children = orderedNodes;

                if (validChildIds.length === 0) {
                    abortLocalDivisionApiMutation();
                    return;
                }

                // Reorder broadcasts DivisionUpdated per child during the in-flight
                // request (skipped via pending count). Do not cache sibling track IDs here
                // or later head changes on those divisions would skip the tree refetch.
                endLocalDivisionApiMutation();
            },
            () => {
                abortLocalDivisionApiMutation();
            },
        );
    }

    function findAndUpdateNodeByParentId(node) {
        if (divisions.value.trackId === node.trackId) {
            const existingChildren = divisions.value.children;
            Object.assign(divisions.value, node);
            if (!node.children?.length && existingChildren?.length) {
                divisions.value.children = existingChildren;
            }
            return;
        }
        const parent = getNodeById(node.parentId);
        if (parent) {
            const childNode = parent.children.find(
                (child) => child.id === node.id,
            );
            if (childNode) {
                const existingChildren = childNode.children;
                Object.assign(childNode, node);
                if (!node.children?.length && existingChildren?.length) {
                    childNode.children = existingChildren;
                }
            }
        }
    }

    function findAndDeleteNodeByParentId(parentId, nodeId) {
        const parent = getNodeById(parentId);
        if (parent) {
            const index = parent.children.findIndex(
                (child) => child.id === nodeId,
            );
            if (index !== -1) {
                parent.children.splice(index, 1);
            }
        }
    }

    function toggleMembership() {
        showMembership.value = !showMembership.value;
    }

    function setShowMembership(value) {
        showMembership.value = value;
    }

    function setSelectedNode(node) {
        selectedNode.value = node;
    }

    async function fetchTopLineDivisions() {
        try {
            const { data } = await axios.get(`/divisions/workspace-categories`);
            topLineDivisions.value = data?.data || [];
        } catch (error) {
            console.error(error);
        }
    }

    function isDirectChildOfRoot(node) {
        if (!node) return false;
        return node.parentId === divisions.value?.trackId;
    }

    return {
        divisions,
        showMembership,
        selectedNode,
        isLoadingData,
        topLineDivisions,

        fetchAllDivisions,
        addFakeDivision,
        createDivision,
        updateDivision,
        deleteDivision,
        updateChildSequence,
        findAndDeleteNodeByParentId,
        destroyFakeDivisionIfNotPersisted,
        fetchTopLineDivisions,
        isDirectChildOfRoot,

        toggleMembership,
        setShowMembership,
        setSelectedNode,
        getNodeById,
        shouldSkipLiveSyncDivisionTreeRefetch,
    };
});
